import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyOptionsWithSpecials } from '../../../utils/currencies';
import { formatNewMoney } from '../../../utils/formatter';
import ContraparteDropdown from '../../../containers/ContraparteDropdown';
import CashflowAreaDropdown from '../../general/js/formTypes/cashflowAreaDropdown';
import SelectorEERR from '../../general/js/formTypes/selectorEERR';
import FeatherIcon from 'feather-icons-react';
import { Plans, FULL_ACCESS_PLANS } from '../../../utils/plans';
import CurrencyChangeRow from '../components/CurrencyChangeRow';

const REPEAT_DAYS = [7, 15, 30, 45, 60, 90];

const InvoiceObligationFormFields = ({
  formData,
  handleInputChange,
  onSelectContraparte,
  handleDescriptionChange,
  handleRecurrenceChange,
  handleDesdeDateChange,
  handleHastaDateChange,
  handleRepeatChange,
  handleRateChange,
  handleDocumentIdChange,
  handleCashflowAreaChange,
  handleFieldKeyChange,
  handleReferenceNumberChange,
  handleInvoiceCurrencyChange,
  handleInvoiceExchangeRateChange,
  companyId,
  companyPlans,
  isSearchingContraparte,
  contraparteSearchRut,
  open,
  initialData,
  disableRecurrence = false,
  showMoreCampos = false,
  setShowMoreCampos,
  shouldFadeIn = false,
  contraparteSessionKey = 'contraparte'
}) => {
  const { t } = useTranslation('configuracion');
  const repeatOptions = useMemo(
    () => REPEAT_DAYS.map((days) => ({
      value: days,
      text: t('obligationModal.repeatEvery', { days }),
    })),
    [t],
  );
  const [isCashflowDropdownOpen, setIsCashflowDropdownOpen] = useState(false);
  const [isEERRDropdownOpen, setIsEERRDropdownOpen] = useState(false);
  const collapsibleFieldsRef = useRef(null);

  // Hide CurrencyChangeRow if transaction has non-CLP currency (it will be shown in main modal instead)
  const shouldShowCurrencyRow = !(initialData && formData.currency && formData.currency !== 'CLP');

  // Auto-scroll when "ver más campos" is expanded
  useEffect(() => {
    if (showMoreCampos && collapsibleFieldsRef.current) {
      // Find the scrollable parent (modal content)
      const findScrollableParent = (element) => {
        if (!element) return null;
        const style = window.getComputedStyle(element);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll' || 
            style.overflow === 'auto' || style.overflow === 'scroll') {
          return element;
        }
        return findScrollableParent(element.parentElement);
      };

      const scrollableParent = findScrollableParent(collapsibleFieldsRef.current);
      
      if (scrollableParent) {
        // Function to calculate and perform scroll
        const performScroll = () => {
          // Check if ref is still valid
          if (!collapsibleFieldsRef.current || !scrollableParent) return;
          
          const collapsibleRect = collapsibleFieldsRef.current.getBoundingClientRect();
          const parentRect = scrollableParent.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          
          // Check for fixed-positioned dropdown menus
          let dropdownScrollNeeded = 0;
          const fixedDropdowns = document.querySelectorAll('.cashflow-area-dropdown.open .dropdown-menu, .obligations-dropdown.open .dropdown-menu, .eerr-selector-dropdown.open .dropdown-menu');
          fixedDropdowns.forEach((dropdown) => {
            const dropdownRect = dropdown.getBoundingClientRect();
            const dropdownBottom = dropdownRect.bottom;
            const viewportBottom = viewportHeight;
            
            // If dropdown extends below the viewport, calculate how much we need to scroll
            if (dropdownBottom > viewportBottom) {
              // Calculate scroll needed to show the full dropdown
              const overflow = dropdownBottom - viewportBottom;
              // Add extra padding to ensure dropdown is fully visible
              dropdownScrollNeeded = Math.max(dropdownScrollNeeded, overflow + 50);
            }
          });
          
          // Base scroll amount for the collapsible section
          const baseScrollAmount = Math.max(0, collapsibleRect.bottom - parentRect.bottom + 100);
          
          // Total scroll needed
          const totalScrollAmount = baseScrollAmount + dropdownScrollNeeded;
          
          if (totalScrollAmount > 0) {
            scrollableParent.scrollBy({
              top: totalScrollAmount,
              behavior: 'smooth'
            });
          }
        };

        // Wait for the content to render, then scroll
        setTimeout(performScroll, 100);
        
        // Use MutationObserver to detect when dropdowns open (check entire document for fixed dropdowns)
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              const target = mutation.target;
              // Check if it's a dropdown container that opened
              if (target.classList.contains('open') && 
                  (target.classList.contains('cashflow-area-dropdown') || target.classList.contains('obligations-dropdown') || target.classList.contains('eerr-selector-dropdown'))) {
                setTimeout(performScroll, 100);
              }
              // Also check if a dropdown menu appeared
              if (target.classList.contains('dropdown-menu') && target.offsetParent !== null) {
                // Check if it's a child of a cashflow, obligations, or EERR dropdown
                const parent = target.closest('.cashflow-area-dropdown, .obligations-dropdown, .eerr-selector-dropdown');
                if (parent && parent.classList.contains('open')) {
                  setTimeout(performScroll, 100);
                }
              }
            }
            // Also check for added nodes (when dropdown menu is added to DOM)
            if (mutation.type === 'childList') {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === 1 && node.classList && 
                    node.classList.contains('dropdown-menu')) {
                  const parent = node.closest('.cashflow-area-dropdown, .obligations-dropdown, .eerr-selector-dropdown');
                  if (parent && parent.classList.contains('open')) {
                    setTimeout(performScroll, 100);
                  }
                }
              });
            }
          });
        });
        
        // Observe all dropdown elements in the collapsible section
        const dropdowns = collapsibleFieldsRef.current.querySelectorAll('.cashflow-area-dropdown, .obligations-dropdown, .eerr-selector-dropdown');
        dropdowns.forEach((dropdown) => {
          observer.observe(dropdown, { 
            attributes: true, 
            attributeFilter: ['class'],
            childList: true,
            subtree: true
          });
        });
        
        // Also observe the modal content for fixed-positioned dropdowns
        const modalContent = scrollableParent.closest('.create-obligation-modal-content');
        if (modalContent) {
          observer.observe(modalContent, {
            childList: true,
            subtree: true
          });
        }
        
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [showMoreCampos]);

  return (
    <>
      {/* Invoice-specific currency and exchange rate row - Above Contraparte */}
      {/* Only show if transaction currency is CLP (non-CLP currencies show in main modal) */}
      {shouldShowCurrencyRow && (
        <CurrencyChangeRow
          formData={formData}
          handleInvoiceCurrencyChange={handleInvoiceCurrencyChange}
          handleInvoiceExchangeRateChange={handleInvoiceExchangeRateChange}
          showReconciliationText={false}
        />
      )}

      {/* Contraparte Field - Second Row */}
      <div className="obligation-field">
        <label className="obligation-label" htmlFor="contraparte">{t('obligationModal.counterpartyOptional')}</label>
        <ContraparteDropdown
          key={contraparteSessionKey}
          id="contraparte"
          value={formData.contraparteId}
          selectedTextOverride={formData.contraparte || ''}
          onChange={onSelectContraparte}
          placeholder={t('obligationModal.enterCounterparty')}
          className="w-100"
          companyId={companyId}
          disabled={false}
          loadingText={isSearchingContraparte && contraparteSearchRut ? t('obligationModal.searching') : null}
        />
      </div>

      {/* Descripción Field - Third Row */}
      <div className="obligation-field">
        <label className="obligation-label" htmlFor="description">{t('obligationModal.detailOptional')}</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={handleDescriptionChange}
          placeholder={t('obligationModal.enterDetail')}
          rows={4}
        />
      </div>

      {/* Ver más campos toggle */}
      <div 
        onClick={() => setShowMoreCampos(!showMoreCampos)}
        style={{ 
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '8px',
          marginBottom: '8px',
          color: '#6B7280',
          whiteSpace: 'nowrap'
        }}
        className="tw-text-xs tw-text-gray-600"
      >
        <span>{t('obligationModal.showMoreFields')}</span>
        <FeatherIcon 
          icon={showMoreCampos ? 'chevron-up' : 'chevron-down'}
          size={14}
        />
      </div>

      {/* Additional Fields - Collapsible */}
      <div 
        ref={collapsibleFieldsRef}
        className={`${showMoreCampos ? "obligation-collapsible-fields" : "obligation-collapsible-fields-hidden"} ${shouldFadeIn ? "fade-in" : ""}`}
      >
        {/* Parametrización Section */}
        <div className="obligation-section-title">{t('obligationModal.parameterization')}</div>
        
        {/* Número o Folio and Recurrencia in same row */}
        <div className="obligation-grid obligation-g-2">
          {/* Número o Folio Field */}
          <div className="obligation-field">
            <label className="obligation-label" htmlFor="document-id">{t('obligationModal.folioOptional')}</label>
            <input 
              id="document-id"
              type="text" 
              value={formData.documentId}
              onChange={handleDocumentIdChange}
              placeholder={t('obligationModal.enterFolio')}
            />
          </div>

          {/* Recurrencia field */}
          <div className="obligation-field">
            <label className="obligation-label" htmlFor="recurrence">{t('obligationModal.recurrence')}<span className="required-asterisk">*</span></label>
            <select 
              id="recurrence" 
              value={formData.recurrence}
              onChange={handleRecurrenceChange}
              disabled={disableRecurrence}
              style={{
                backgroundColor: disableRecurrence ? '#f9fafb' : '#fff',
                cursor: disableRecurrence ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="unica">{t('obligationModal.recurrenceOnce')}</option>
              <option value="recurrente">{t('obligationModal.recurrenceRecurring')}</option>
            </select>
          </div>
        </div>

        {/* Conditional fields for recurrent obligations */}
        {formData.recurrence === 'recurrente' && (
          <>
            <div className="obligation-grid obligation-g-3">
              <div className="obligation-field">
                <label className="obligation-label" htmlFor="desde-date">{t('obligationModal.dateFrom')}<span className="required-asterisk">*</span></label>
                <input
                  id="desde-date"
                  type="date"
                  value={formData.desdeDate}
                  onChange={handleDesdeDateChange}
                  max={formData.hastaDate}
                  placeholder={t('obligationModal.startDatePlaceholder')}
                  required
                />
              </div>
              <div className="obligation-field">
                <label className="obligation-label" htmlFor="hasta-date">{t('obligationModal.dateTo')}<span className="required-asterisk">*</span></label>
                <input
                  id="hasta-date"
                  type="date"
                  value={formData.hastaDate}
                  onChange={handleHastaDateChange}
                  min={formData.desdeDate}
                  placeholder={t('obligationModal.endDatePlaceholder')}
                  required
                />
              </div>
              <div className="obligation-field">
                <label className="obligation-label" htmlFor="repeat">{t('obligationModal.repeat')}<span className="required-asterisk">*</span></label>
                <select
                  id="repeat"
                  value={formData.repeat}
                  onChange={handleRepeatChange}
                  required
                >
                  {repeatOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {formData.desdeDate && formData.hastaDate && formData.desdeDate > formData.hastaDate && (
              <div className="obligation-error-message" style={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '8px 12px',
                borderRadius: '4px',
                marginBottom: '10px',
                border: '1px solid #ffcdd2',
                fontSize: '12px'
              }}>
                {t('obligationModal.dateRangeError')}
              </div>
            )}
          </>
        )}

        {/* Área Flujo de Caja and Área Estado de Resultados in same row */}
        <div className="obligation-grid obligation-g-2">
          <div 
            className="obligation-field"
            hidden={companyPlans.includes(Plans.flujo_de_caja) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
          >
            <label className="obligation-label">
              {(formData.obligationType === 'estimaciondeingreso' || formData.obligationType === 'estimaciondeegreso')
                ? t('obligationModal.cashflowArea')
                : t('obligationModal.cashflowAreaOptional')}
              {(formData.obligationType === 'estimaciondeingreso' || formData.obligationType === 'estimaciondeegreso') 
                ? <span className="required-asterisk">*</span>
                : null
              }
            </label>
            <CashflowAreaDropdown
              editData={{ cashflowAreaId: formData.cashflowAreaId }}
              onChange={handleCashflowAreaChange}
              placeholder={t('obligationModal.selectCashflowArea')}
              enabled={open}
              onOpenChange={setIsCashflowDropdownOpen}
            />
          </div>

          <div 
            className="obligation-field"
            hidden={companyPlans.includes(Plans.estado_de_resultados) == false && !FULL_ACCESS_PLANS.some(p => companyPlans.includes(p))}
          >
            <label className="obligation-label">{t('obligationModal.plAreaOptional')}</label>
            <SelectorEERR
              value={formData.fieldKey}
              onChange={(_, v) => handleFieldKeyChange(v.value)}
              placeholder={t('obligationModal.selectPlArea')}
              companyId={companyId}
              onOpenChange={setIsEERRDropdownOpen}
            />
          </div>
        </div>

        {/* Invisible spacer when cashflow or EERR dropdown is open */}
        {(isCashflowDropdownOpen || isEERRDropdownOpen) && (
          <input
            type="text"
            style={{
              visibility: 'hidden',
              height: '450px',
              width: '100%',
              border: 'none',
              padding: 0,
              margin: 0,
              pointerEvents: 'none',
              position: 'relative',
              zIndex: -1
            }}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );
};

export default InvoiceObligationFormFields;

